import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Box, Stack, Typography } from "@mui/material"
import { useTheme } from "@mui/material/styles"

import type { ContextDevice } from "types/device.types"

import { sortDevicesBySeverity } from "helpers/utils/devices"
import NoDeviceIcon from "icons/NoDeviceIcon"
import DeviceOverflowChip from "widgets/common/DeviceOverflowChip"
import DeviceStatusMarks from "widgets/common/DeviceStatusMarks"
import EntityLink from "widgets/common/EntityLink"

const VISIBLE_DEVICES = 2

interface DevicesCellProps {
  devices: ContextDevice[]
  /** Set on a device page: the devices are the other ones on that asset. */
  siblingsOf?: "span" | "tower"
}

/** The most severe devices by name, with a +N for the rest, or why there are none. */
const DevicesCell = ({ devices, siblingsOf }: DevicesCellProps) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const sortedDevices = useMemo(() => sortDevicesBySeverity(devices), [devices])

  if (sortedDevices.length === 0 && siblingsOf) {
    return (
      <Typography color="text.secondary">
        {t(
          siblingsOf === "span"
            ? "asset_context.ONLY_DEVICE_ON_SPAN"
            : "asset_context.ONLY_DEVICE_ON_TOWER",
        )}
      </Typography>
    )
  }

  if (sortedDevices.length === 0) {
    return (
      <Stack direction="row" alignItems="center" gap={1}>
        <NoDeviceIcon color={theme.palette.text.secondary} width={20} height={20} />
        <Typography color="text.secondary">
          {t("asset_context.NO_DEVICE_INSTALLED")}
        </Typography>
      </Stack>
    )
  }

  return (
    // One row: a long name gives way with an ellipsis, the status and the chip never do.
    <Stack direction="row" alignItems="center" columnGap={2} minWidth={0}>
      {sortedDevices.slice(0, VISIBLE_DEVICES).map((device) => (
        <Stack key={device.id} direction="row" alignItems="center" gap={0.5} minWidth={0}>
          <EntityLink entity="device" id={device.id} name={device.name} maxLines={1} />
          <DeviceStatusMarks status={device.status} hasAlert={device.hasAlert} />
        </Stack>
      ))}
      {sortedDevices.length > VISIBLE_DEVICES && (
        <Box flexShrink={0}>
          <DeviceOverflowChip devices={sortedDevices} visibleCount={VISIBLE_DEVICES} />
        </Box>
      )}
    </Stack>
  )
}

export default DevicesCell
